
import { useState, useEffect } from 'react';
import { Parent, Student } from '@/types/models';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { BillingStatus } from '@/types/constants';
import { FamilyService } from '@/services/familyService';

export const useFamilyDetailActions = (
  familyId: string, 
  organizationId: string | undefined,
  family: any,
  setFamily: (family: any) => void,
  parents: Parent[],
  setParents: (parents: Parent[]) => void,
  handleAddParent: (data: any) => Promise<any>,
  handleAddStudent: (data: any) => Promise<any>,
  handleDeleteFamily: () => Promise<boolean>,
  setEditingParent?: (parent: Parent | null) => void,
  setIsEditParentDialogOpen?: (isOpen: boolean) => void,
  setEditingStudent?: (student: Student | null) => void,
  setIsEditStudentDialogOpen?: (isOpen: boolean) => void
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddParentDialogOpen, setIsAddParentDialogOpen] = useState(false);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check URL parameters on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');
    
    if (action === 'add-parent') {
      setIsAddParentDialogOpen(true);
      // Remove the query parameter after opening the dialog
      navigate(`/admin/families/${familyId}`, { replace: true });
    } else if (action === 'add-student') {
      setIsAddStudentDialogOpen(true);
      // Remove the query parameter after opening the dialog
      navigate(`/admin/families/${familyId}`, { replace: true });
    }
  }, [location.search, familyId, navigate]);

  const handleBackClick = () => navigate('/admin/families');
  const handleAddParentClick = () => setIsAddParentDialogOpen(true);
  const handleAddStudentClick = () => setIsAddStudentDialogOpen(true);

  const handleParentAdded = async (parentData: any, continueToDetails?: boolean) => {
    if (!familyId || !organizationId) return;
    
    try {
      const data = {
        ...parentData,
        family_id: familyId,
        organization_id: organizationId
      };
      
      const newParent = await handleAddParent(data);
      setIsAddParentDialogOpen(false);
      toast.success("Parent added successfully");

      if (continueToDetails && newParent && setEditingParent && setIsEditParentDialogOpen) {
        // Open the edit parent dialog with the newly created parent
        setEditingParent(newParent);
        setIsEditParentDialogOpen(true);
      }
    } catch (error) {
      console.error("Error adding parent:", error);
      toast.error("Failed to add parent");
    }
  };

  const handleStudentAdded = async (studentData: any, continueToDetails?: boolean) => {
    if (!familyId || !organizationId) return;
    
    try {
      const data = {
        ...studentData,
        family_id: familyId,
        organization_id: organizationId
      };
      
      const newStudent = await handleAddStudent(data);
      setIsAddStudentDialogOpen(false);
      toast.success("Student added successfully");

      if (continueToDetails && newStudent && setEditingStudent && setIsEditStudentDialogOpen) {
        // Open the edit student dialog with the newly created student
        setEditingStudent(newStudent);
        setIsEditStudentDialogOpen(true);
      }
    } catch (error) {
      console.error("Error adding student:", error);
      toast.error("Failed to add student");
    }
  };

  const handleDeleteFamilyClick = async () => {
    setIsDeleting(true);
    try {
      if (await handleDeleteFamily()) {
        // Navigate with a flag to trigger refresh
        navigate('/admin/families', { replace: true });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActiveStatus = async (isActive: boolean) => {
    if (!family) return;
    
    try {
      const updatedFamily = await FamilyService.setFamilyActiveStatus(family.id, isActive);
      
      if (updatedFamily) {
        setFamily(updatedFamily);
        toast.success(
          isActive ? 'Family set to active' : 'Family set to inactive', 
          { description: `${family.name} has been updated successfully.` }
        );
      }
    } catch (error) {
      console.error("Error toggling family active status:", error);
      toast.error("Failed to update family status");
    }
  };

  const handleUpdateBillingStatus = async (billingStatus: BillingStatus) => {
    if (!family) return;
    
    try {
      const updatedFamily = await FamilyService.updateFamilyBillingStatus(
        family.id, 
        billingStatus
      );
      
      if (updatedFamily) {
        setFamily(updatedFamily);
        toast.success(
          `Billing status updated to ${billingStatus}`, 
          { description: `${family.name}'s billing status has been updated.` }
        );
      }
    } catch (error) {
      console.error("Error updating family billing status:", error);
      toast.error("Failed to update billing status");
    }
  };

  return {
    activeTab,
    setActiveTab,
    isAddParentDialogOpen,
    setIsAddParentDialogOpen,
    isAddStudentDialogOpen,
    setIsAddStudentDialogOpen,
    isDeleting,
    handleBackClick,
    handleAddParentClick,
    handleAddStudentClick,
    handleParentAdded,
    handleStudentAdded,
    handleDeleteFamilyClick,
    handleToggleActiveStatus,
    handleUpdateBillingStatus
  };
};
